import React from 'react';

const formatPackagingType = (value) => {
  if (!value) return 'Individual';
  return value
    .toString()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatPalletType = (value) => value === 'multi' ? 'Multi SKU' : 'Single SKU';

const OUTBOUND_TYPES = ['ship', 'pick_pack', 'kit_build'];

const RequestItemsSection = ({ request, embedded = false }) => {
  const isOutbound = OUTBOUND_TYPES.includes(request?.requestType);
  const shippedQuantityByLineId = new Map();
  if (isOutbound) {
    const shipmentsById = new Map();
    [
      ...(Array.isArray(request?.Shipments) ? request.Shipments : []),
      ...(request?.Shipment ? [request.Shipment] : [])
    ].forEach((shipment, index) => {
      if (!shipment) return;
      shipmentsById.set(shipment.id || `shipment-${index}`, shipment);
    });

    shipmentsById.forEach((shipment) => {
      (shipment.LineItems || []).forEach((line) => {
        const lineId = line.request_line_item_id || line.requestLineItemId || line.RequestLineItem?.id;
        if (!lineId) return;
        shippedQuantityByLineId.set(
          lineId,
          (shippedQuantityByLineId.get(lineId) || 0) + Number(line.quantity_shipped || 0)
        );
      });
    });
  }

  const getItemProgress = (item) => {
    const orderedQty = Number(item.quantityOnPallet ?? item.quantity ?? 0);
    if (isOutbound) {
      const shippedQty = Number(shippedQuantityByLineId.get(item.id) || 0);
      return {
        orderedQty,
        receivedQty: shippedQty,
        remainingQty: Math.max(orderedQty - shippedQty, 0)
      };
    }

    // When the item comes from a PalletItem, use its per-pallet
    // quantity_received (tracks exactly how many were checked in
    // on THIS pallet). Fall back to the RequestLineItem total for
    // ungrouped items or legacy data without the per-pallet field.
    const receivedQty = item.quantityReceivedOnPallet != null
      ? Number(item.quantityReceivedOnPallet)
      : Number(item.qtyReceived ?? item.qty_received ?? 0);
    return {
      orderedQty,
      receivedQty,
      remainingQty: Math.max(orderedQty - receivedQty, 0)
    };
  };

  const getPalletProgress = (items) => items.reduce((totals, item) => {
    const { orderedQty, receivedQty } = getItemProgress(item);
    return {
      orderedQty: totals.orderedQty + orderedQty,
      receivedQty: totals.receivedQty + receivedQty
    };
  }, { orderedQty: 0, receivedQty: 0 });

  const getStatusBadge = (status) => {
    const statusColors = {
      PENDING_RECEIVE: 'bg-yellow-100 text-yellow-800',
      PARTIALLY_RECEIVED: 'bg-blue-100 text-blue-800',
      PARTIALLY_RECEIVED_WITH_HOLD: 'bg-orange-100 text-orange-800',
      IN_HOLD: 'bg-red-100 text-red-800',
      RECEIVED: 'bg-green-100 text-green-800',
      CANCELLED: 'bg-gray-100 text-gray-800',
      PENDING: 'bg-yellow-100 text-yellow-800',
      APPROVED: 'bg-green-100 text-green-800',
      IN_PROGRESS: 'bg-blue-100 text-blue-800',
      COMPLETED: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      shipped: 'bg-green-100 text-green-800'
    };

    // For outbound requests, map generic statuses to shipping-oriented labels
    let displayLabel = status?.replace(/_/g, ' ') || 'Pending';
    if (isOutbound) {
      if (status === 'PENDING' || status === 'pending') displayLabel = 'Pending Shipment';
      if (status === 'APPROVED') displayLabel = 'Pending Shipment';
      if (status === 'IN_PROGRESS' || status === 'in_progress') displayLabel = 'Partially Shipped';
      if (status === 'COMPLETED' || status === 'completed' || status === 'shipped') displayLabel = 'Shipped';
    }

    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {displayLabel}
      </span>
    );
  };

  const pallets = request?.Pallets || [];
  const palletGroups = pallets
    .map((pallet, index) => ({
      pallet,
      palletNumber: index + 1,
      items: (pallet.PalletItems || [])
        .map((palletItem) => {
          const lineItem = palletItem.RequestLineItem;
          if (!lineItem) return null;
          return {
            ...lineItem,
            quantityOnPallet: palletItem.quantity_on_pallet || palletItem.quantityOnPallet || lineItem.quantity || 0,
            quantityReceivedOnPallet: palletItem.quantity_received ?? palletItem.quantityReceived ?? null
          };
        })
        .filter(Boolean)
    }))
    .filter((group) => group.items.length > 0);

  const groupedIds = new Set(
    palletGroups.flatMap((group) => group.items.map((item) => item.id)).filter(Boolean)
  );
  const ungroupedItems = (request?.RequestLineItems || []).filter((item) => !groupedIds.has(item.id));

  const renderItemRows = (items) => (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Item</th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Packaging Type</th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Qty</th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {items.map((item, index) => {
            const progress = getItemProgress(item);
            const effectiveStatus = isOutbound
              ? (progress.receivedQty >= progress.orderedQty && progress.orderedQty > 0
                ? 'completed'
                : (progress.receivedQty > 0 ? 'in_progress' : item.status))
              : item.status;

            return (
              <tr key={item.id || index}>
                <td className="px-4 py-3 align-top">
                  <div className="text-sm font-medium text-gray-900">{item.productName || '-'}</div>
                  <div className="text-sm text-gray-500">{item.productSku || '-'}</div>
                </td>
                <td className="px-4 py-3 align-top text-sm text-gray-600">
                  <div>{formatPackagingType(item.packagingType)}</div>
                  {item.cartonCount ? (
                    <div className="text-xs text-gray-500">
                      {item.cartonCount} carton{Number(item.cartonCount) === 1 ? '' : 's'}
                    </div>
                  ) : null}
                </td>
                <td className="px-4 py-3 align-top text-sm text-gray-600">
                  {(item.quantityOnPallet ?? item.quantity ?? 0)}
                  {item.unitOfMeasure ? ` ${item.unitOfMeasure}` : ''}
                </td>
                <td className="px-4 py-3 align-top">
                  {getStatusBadge(effectiveStatus)}
                  <div className="mt-2 text-xs text-gray-500">
                    {isOutbound ? 'Shipped' : 'Received'} {progress.receivedQty} of {progress.orderedQty}
                  </div>
                  {progress.remainingQty > 0 && (
                    <div className="mt-1 h-1.5 w-28 overflow-hidden rounded-full bg-gray-200">
                      <div
                        className="h-full rounded-full bg-brand-gold"
                        style={{
                          width: `${progress.orderedQty > 0
                            ? Math.min((progress.receivedQty / progress.orderedQty) * 100, 100)
                            : 0}%`
                        }}
                      />
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderPalletMeta = (pallet) => {
    const hasDimensions = [pallet.length, pallet.width, pallet.height].every(
      (value) => value !== null && value !== undefined && value !== ''
    );
    const palletDimensions = hasDimensions ? `${pallet.length} × ${pallet.width} × ${pallet.height} in` : '—';

    return (
      <div className="mb-4 space-y-1.5 text-xs text-gray-600">
        <div className="grid gap-1 sm:grid-cols-2 sm:gap-4">
          <div>
            <span className="font-medium text-gray-700">Pallet Type:</span>{' '}
            <span>{formatPalletType(pallet.type)}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Depalletize:</span>{' '}
            <span>{pallet.depalletize_on_arrival ? 'Yes' : 'No'}</span>
          </div>
        </div>
        <div className="grid gap-1 sm:grid-cols-2 sm:gap-4">
          <div>
            <span className="font-medium text-gray-700">Pallet Weight:</span>{' '}
            <span>{pallet.weight_lbs ? `${pallet.weight_lbs} lbs` : '—'}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">Pallet Dimensions:</span>{' '}
            <span>{palletDimensions}</span>
          </div>
        </div>
        <div>
          <span className="font-medium text-gray-700">Notes:</span>{' '}
          <span>{pallet.notes || '—'}</span>
        </div>
      </div>
    );
  };

  const body = (
    <>
      {!embedded && (
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Request Items</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Items included in this request{palletGroups.length > 0 ? ', organized by pallet' : ''}.
          </p>
        </div>
      )}

      <div className={embedded ? '' : 'border-t border-gray-200'}>
        <div className="space-y-6 py-5 sm:py-6">
          {!palletGroups.length && !ungroupedItems.length && (
            <div className="text-center text-sm text-gray-500">
              <p className="text-lg font-medium text-gray-900">No items found</p>
              <p className="mt-1">This request does not have any items associated with it.</p>
            </div>
          )}

          {palletGroups.map((group) => (
            <div key={group.pallet.id || group.pallet.pallet_label} className="border-b border-gray-200 pb-6 last:border-b-0 last:pb-0">
              {(() => {
                const palletProgress = getPalletProgress(group.items);
                return (
              <div className="px-4 pb-3 sm:px-6">
                <h4 className="text-base font-semibold text-gray-900">
                  Pallet #{group.palletNumber} - {group.pallet.pallet_label || group.pallet.label || 'Unlabeled Pallet'}
                </h4>
                <p className="mt-1 text-sm text-gray-500">
                  Items included on this pallet. {isOutbound ? 'Shipped' : 'Received'} {palletProgress.receivedQty} of {palletProgress.orderedQty}.
                </p>
              </div>
                );
              })()}
              <div className="px-4 sm:px-6">
                {renderPalletMeta(group.pallet)}
              </div>
              {renderItemRows(group.items)}
            </div>
          ))}

          {ungroupedItems.length > 0 && (
            <div className={palletGroups.length > 0 || !embedded ? 'overflow-hidden rounded-lg border border-gray-200' : ''}>
              {(palletGroups.length > 0 || !embedded) && (
                <div className="border-b border-gray-200 bg-gray-50 px-4 py-4">
                  <h4 className="text-base font-semibold text-gray-900">
                    {palletGroups.length > 0 ? 'Items Not Assigned to a Pallet' : 'Request Items'}
                  </h4>
                  <p className="mt-1 text-sm text-gray-500">
                    {palletGroups.length > 0
                      ? 'Items included in this request outside of a pallet.'
                      : 'Items included in this request.'}
                  </p>
                </div>
              )}
              <div className={palletGroups.length > 0 || !embedded ? '' : 'border-t border-gray-200'}>
                {renderItemRows(ungroupedItems)}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );

  if (embedded) return body;

  return (
    <div className="mb-6 overflow-hidden rounded-lg bg-white shadow sm:rounded-lg">
      {body}
    </div>
  );
};

export default RequestItemsSection;
